import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { Users, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

interface SearchResult {
  id: string;
  type: "lead" | "deal";
  title: string;
  subtitle: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 300);
  const navigate = useNavigate();
  const { currentTenant } = useAuth();

  // Global shortcut CMD+K / CTRL+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Search when debounced value changes
  const performSearch = useCallback(
    async (query: string) => {
      if (!query || query.length < 2 || !currentTenant?.id) {
        setResults([]);
        return;
      }

      setIsSearching(true);

      try {
        const [leadsRes, dealsRes] = await Promise.all([
          supabase
            .from("leads")
            .select("id, name, phone, email")
            .eq("tenant_id", currentTenant.id)
            .or(`name.ilike.%${query}%,phone.ilike.%${query}%,email.ilike.%${query}%`)
            .limit(5),
          supabase
            .from("deals")
            .select("id, code, name, value, lead:leads(name)")
            .eq("tenant_id", currentTenant.id)
            .or(`name.ilike.%${query}%`)
            .limit(5),
        ]);

        const leadResults: SearchResult[] = (leadsRes.data || []).map((l) => ({
          id: l.id,
          type: "lead" as const,
          title: l.name,
          subtitle: l.phone || l.email || "",
        }));

        const dealResults: SearchResult[] = (dealsRes.data || []).map((d: any) => ({
          id: d.id,
          type: "deal" as const,
          title: d.lead?.name || d.name || `Deal #${d.code}`,
          subtitle: d.value
            ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(d.value)
            : `#${d.code}`,
        }));

        setResults([...leadResults, ...dealResults]);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setIsSearching(false);
      }
    },
    [currentTenant?.id]
  );

  useEffect(() => {
    performSearch(debouncedSearch);
  }, [debouncedSearch, performSearch]);

  const handleSelect = (result: SearchResult) => {
    setOpen(false);
    setSearchTerm("");
    setResults([]);
    if (result.type === "lead") {
      navigate(`/app/crm/lead/${result.id}`);
    } else {
      navigate(`/app/crm/oportunidade/${result.id}`);
    }
  };

  const leads = results.filter((r) => r.type === "lead");
  const deals = results.filter((r) => r.type === "deal");

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Buscar leads, oportunidades..."
        value={searchTerm}
        onValueChange={setSearchTerm}
      />
      <CommandList>
        <CommandEmpty>
          {isSearching ? "Buscando..." : searchTerm.length < 2 ? "Digite pelo menos 2 caracteres..." : "Nenhum resultado encontrado."}
        </CommandEmpty>

        {leads.length > 0 && (
          <CommandGroup heading="Leads">
            {leads.map((r) => (
              <CommandItem key={r.id} onSelect={() => handleSelect(r)} className="cursor-pointer">
                <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{r.title}</span>
                  {r.subtitle && (
                    <span className="ml-2 text-xs text-muted-foreground">{r.subtitle}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {leads.length > 0 && deals.length > 0 && <CommandSeparator />}

        {deals.length > 0 && (
          <CommandGroup heading="Oportunidades">
            {deals.map((r) => (
              <CommandItem key={r.id} onSelect={() => handleSelect(r)} className="cursor-pointer">
                <Target className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{r.title}</span>
                  {r.subtitle && (
                    <span className="ml-2 text-xs text-muted-foreground">{r.subtitle}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
